import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS, Transform } from "@dnd-kit/utilities";
import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  UseFieldArrayAppend,
  useFormContext,
} from "react-hook-form";
import { v4 as uuid } from "uuid";

import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Icon } from "src/base-components/Icon";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import {
  ManualReviewFile,
  ManualReviewFileTypeEnum,
} from "src/clients/flow-api";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { ManualReviewForm } from "src/manualReviewNode/ManualReviewNodeEditor";
import { HighlightsInner } from "src/manualReviewNode/types";

type Highlight = ManualReviewForm["highlights"][number] & { id: string };
type Highlights = Highlight[];

const FileRow: React.FC<{
  handleRemove: () => void;
  id: string;
  immutable: boolean;
  index: number;
}> = ({ handleRemove, id, immutable, index }) => {
  const { listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(
      transform ? ({ ...transform, scaleY: 1 } as Transform) : null,
    ),
    transition,
  };
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<ManualReviewForm>();

  const fieldErrors = errors?.highlights?.[
    index
  ] as unknown as ManualReviewFile;
  const fieldId = getValues(`highlights.${index}.id_value`);
  const isSearchHighlighted = useIsFieldHighlighted(fieldId);

  return (
    <li
      ref={setNodeRef}
      className="flex w-full flex-row items-start gap-x-4"
      style={style}
    >
      <ReorderHandle
        className="mt-1.5"
        immutable={immutable}
        listeners={listeners}
      />
      <div className="flex w-full items-center gap-x-4">
        <Controller
          control={control}
          name={`highlights.${index}.value`}
          render={({ field }) => (
            <AutocompleteCodeInput
              dataLoc={`manual-review-file-${index}-type`}
              disabled={immutable}
              error={fieldErrors?.value}
              isSearchHighlighted={isSearchHighlighted}
              placeholder="data.file_id"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="mt-1.5">
        <Icon
          color="text-gray-400"
          icon={faTrashCan}
          padding={false}
          size="xs"
          onClick={!immutable ? handleRemove : undefined}
        />
      </div>
    </li>
  );
};

const AddFileButton: React.FC<{
  append: UseFieldArrayAppend<ManualReviewForm, "highlights">;
  immutable: boolean;
}> = ({ append, immutable }) => {
  return (
    <Button
      dataLoc="add-manual-review-element"
      disabled={immutable}
      iconLeft={faPlus}
      size="sm"
      variant="secondary"
      onClick={() => {
        append({
          type: ManualReviewFileTypeEnum.FILE,
          id_value: uuid(),
          value: "",
        });
      }}
    >
      Add file
    </Button>
  );
};

export const FilesEditor: React.FC<{
  immutable: boolean;
  fields: Highlights;
  remove: (index: number) => void;
  append: (value: ManualReviewForm["highlights"][number]) => void;
  move: (from: number, to: number) => void;
}> = ({ immutable, fields, remove, append, move }) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const fieldToMove = fields.findIndex((field) => field.id === active.id);
      const fieldToInsert = fields.findIndex((field) => field.id === over.id);
      if (fieldToMove !== -1 && fieldToInsert !== -1) {
        move(fieldToMove, fieldToInsert);
      }
    }
  };

  return (
    <Card className="mb-6 pt-4" data-loc="manual-review-files-editor">
      {fields.some((field) => field.type === ManualReviewFileTypeEnum.FILE) && (
        <ol className="mb-4 flex flex-col gap-y-4 px-4">
          <DndContext onDragEnd={handleDragEnd}>
            <SortableContext items={fields.map((file) => file.id)}>
              {fields.map((highlight, index) => {
                if (highlight.type !== ManualReviewFileTypeEnum.FILE)
                  return null;
                return (
                  <FileRow
                    key={highlight.id}
                    handleRemove={() => remove(index)}
                    id={highlight.id}
                    immutable={immutable}
                    index={index}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </ol>
      )}
      <AddFileButton
        append={(value) => append(value as HighlightsInner)}
        immutable={immutable}
      />
    </Card>
  );
};
